/* eslint react/no-danger: 0 */
import React from 'react';
import moment from 'moment';
import mobile from 'is-mobile';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Hidden from '@material-ui/core/Hidden';

import QRCode from 'qrcode';
import config from '@/configs/config';
import ProgressBackdrop from '@/components/ProgressBackdrop';

import Alert from '@/components/Alert';
import firebase from '@/services/firebase';
import useMultiFirebaseDatabaseQuery from '@/hooks/useMultiFirebaseDatabaseQuery';
import { useFridgeContext } from '@/contexts/useFridgeContext';
import { useAuthState } from '@/plugins/Auth';
import helper, { DELIVERY_METHOD } from '@/helper';
import { addFriendPageUrl } from '../../../../pages/AboutUs/const';
import Rule from '../../PreOrderPage/components/Detail/Rule';

const ExpandableContent = styled.div`
  &:before{
    content: '感謝您的訂購，提醒您請務必注意以下取貨辦法 ▼';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    line-height: 40px;
    text-align: center;
    cursor: pointer;
    font-size: 14px;
    font-weight: bold;
  }
  position: relative;
  background-color: rgb(242, 242, 242);
  overflow: hidden;
  padding: 20px;
  height: 20px;
  &.open {
    &:before{
      content: '感謝您的訂購，提醒您請務必注意以下取貨辦法 ▲';
    }
    height: auto;
  }
`;

const Receipt = () => {
  const theme = useTheme();
  const isInDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  const { user } = useAuthState();
  const [addFriendPageUrlQRCode, setAddFriendPageUrlQRCode] = React.useState('');
  const [alert, setAlert] = React.useState(null);
  const history = useHistory();
  const { fridgeDictionary } = useFridgeContext();
  const {
    checkoutData = [],
    orderId = null,
    total = 0,
    totalDeliveryFee = 0,
  } = history?.location?.state || {};

  const preOrderIds = React.useMemo(() => Object.keys(
    checkoutData
      .map(({ preOrderId }) => preOrderId)
      .reduce((result, preOrderId) => ({ ...result, [preOrderId]: true }), {})
  ), [checkoutData]);
  const preOrderIndexes = React.useMemo(() => preOrderIds.reduce((result, preOrderId, index) => {
    result[preOrderId] = index;
    return result;
  }, {}), [preOrderIds]);
  const { isLoading, data: preOrders } = useMultiFirebaseDatabaseQuery(
    preOrderIds.map((preOrderId) => firebase.database().ref(`/${process.env.REACT_APP_MODE}/pre-order/preOrderEvents/${preOrderId}`))
  );

  // Pushing data to dataLayer (i.e, sending checkout data to GA)
  React.useEffect(() => {
    try {
      if (isLoading || !orderId) return;
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'transaction',
        currencyCode: 'TWD',
        currency: 'TWD',
        ecommerce: {
          purchase: {
            actionField: {
              id: orderId,
              affiliation: 'online',
              revenue: total,
              shipping: totalDeliveryFee,
              transactionDiscount: '',
              CouponDiscount: '',
              CouponCode: '',
            },
            products: checkoutData.map((data) => {
              const {
                amount,
                fridgeId,
                preOrderId,
              } = data;
              const preOrderIndex = preOrderIndexes[preOrderId];
              const preOrder = preOrders[preOrderIndex];
              const {
                title,
                provider,
                productId,
                tag,
              } = preOrder;
              return {
                providertId: '',
                provider,
                productId,
                name: title,
                price: helper.getBasePrice(preOrder),
                quantity: amount,
                preOrderFridgeId: fridgeId,
                campaignDiscount: '',
                campaignCode: '',
                category: tag,
              };
            }),
          },
        },
      });
    } catch (error) {
      console.error(error);
    }
  }, [
    isLoading,
    preOrders,
    orderId,
    checkoutData,
    preOrderIndexes,
    total,
    totalDeliveryFee,
  ]);

  React.useEffect(() => {
    if (user?.isWaitingForAddingFriend && orderId) {
      const targetUrl = helper.genRedirectUrlWithTrackingInfo(addFriendPageUrl, 'visit_addfriend_page');

      QRCode.toDataURL(
        targetUrl,
        (_err, url) => {
          setAddFriendPageUrlQRCode(url);
        }
      );

      setAlert({
        title: `輕鬆追蹤到貨進度，加入${config.BrandNameTW}LINE好友`,
        message: (
          <Grid container spacing={3} wrap="nowrap">
            {!mobile()
                && (
                  <Grid item>
                    <img src={addFriendPageUrlQRCode} alt="" width="100" height="100" />
                  </Grid>
                )}
            <Grid item style={{ textAlign: 'left' }}>
              <Box>
                擔心忘記取貨嗎？加入
                {config.BrandNameTW}
                官方帳號，我們將發送貨到通知提醒您記得取貨。
              </Box>
              <Box mt={1}>
                還有活動訊息和不定期優惠！
              </Box>
            </Grid>
          </Grid>
        ),
        buttons: (
          <>
            <Button fullWidth onClick={() => setAlert(null)}>
              <Box fontWeight="fontWeightBold" color={mobile() ? 'gray' : 'support.main'}>
                我加好囉！
              </Box>
            </Button>
            {mobile()
            && (
            <Button
              fullWidth
              onClick={() => setAlert(null)}
            >
              <a href={targetUrl} target="_blank" rel="noopener noreferrer">
                <Box fontWeight="fontWeightBold" color="support.main">
                  現在加入
                </Box>
              </a>
            </Button>
            )}
          </>
        ),
      });
    }
  }, [user, orderId, addFriendPageUrlQRCode]);

  const receiptHeader = (
    <>
      <Hidden only="xs">
        <Box position="relative" width={1} height={120} my={2}>
          <img
            src="/images/PreOrder/checkout_banner_desktop.jpg"
            style={{ objectFit: 'cover', height: '100%', width: '100%' }}
            alt=""
          />
        </Box>
        <Box fontWeight="fontWeightBold" fontSize="20px" pl="210px">恭喜您已完成預購</Box>
        <Box pl="210px">以下為您的訂購資訊</Box>
      </Hidden>
      <Hidden only="sm">
        <Box position="relative">
          <Box bgcolor="rgb(242, 242, 242)">
            <img src="/images/PreOrder/background.png" style={{ width: '100%', maxHeight: 300 }} alt="" />
          </Box>
          <Box
            position="absolute"
            width={1}
            bottom={60}
          >
            <Grid container direction="column" justify="center" spacing={1}>
              <Grid item>
                <Box
                  color="primary.main"
                  fontSize="h5.fontSize"
                  fontWeight="fontWeightBold"
                  textAlign="center"
                >
                  恭喜您已完成預購
                </Box>
              </Grid>
              <Grid item>
                <Box
                  color="common.white"
                  fontSize="body1.fontSize"
                  fontWeight="fontWeightBold"
                  textAlign="center"
                >
                  以下為您的訂購資訊
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Hidden>
    </>
  );

  const receiptContent = (
    <Box
      py={2}
      mb={2}
    >
      {
        checkoutData.map((data) => {
          const preOrderIndex = preOrderIndexes[data?.preOrderId];
          const preOrder = preOrders?.[preOrderIndex];
          const amount = data?.amount;
          const displayingOrderId = data?.orderId || orderId || null;
          const title = data?.title || preOrder?.title || '';
          const coverImage = data?.coverImage || preOrder?.coverImage || '';
          return [
            <Box display="flex" my={1} key={preOrderIndex}>
              <Hidden only="xs">
                <img
                  style={{
                    objectFit: 'cover',
                    display: 'inline-block',
                    verticalAlign: 'top',
                    marginRight: '10px',
                    width: '200px',
                    height: '200px',
                  }}
                  src={coverImage ? `${config.RESOURCE_URI}${coverImage}` : ''}
                  alt=""
                />
              </Hidden>
              <Box bgcolor="rgb(242, 242, 242)" p={3} flex="1" color="rgb(83,83,83)">
                <Box fontWeight="bold" color="black">預購訂單明細（已完成結帳）</Box>
                <Hidden only="sm"><hr /></Hidden>
                <Grid container spacing={1} style={{ justifyContent: 'space-between', marginTop: 5 }}>
                  <Grid item>預購商品</Grid>
                  <Grid item>{title}</Grid>
                </Grid>
                <Grid container spacing={1} style={{ justifyContent: 'space-between', marginTop: 5 }}>
                  <Grid item>預購數量:</Grid>
                  <Grid item>{amount}</Grid>
                </Grid>
                {
                  displayingOrderId && (
                    <Grid container spacing={1} style={{ justifyContent: 'space-between', marginTop: 5 }}>
                      <Grid item>訂單編號:</Grid>
                      <Grid item>{displayingOrderId}</Grid>
                    </Grid>
                  )
                }
                {
                  data?.fridgeId === DELIVERY_METHOD.DELIVERY ? (
                    <>
                      <Grid container spacing={1} style={{ justifyContent: 'space-between', marginTop: 5 }}>
                        <Grid item>指定取貨方式:</Grid>
                        <Grid item>
                          <Box textAlign="right">宅配</Box>
                          <Box textAlign="right">{`${data?.delivery?.district || ''} ${data?.delivery?.address || ''}` }</Box>
                          <Box textAlign="right">{`${data?.delivery?.date || data?.date || ''} ${data?.delivery?.timeSlot || data?.timeSlot || ''}` }</Box>
                        </Grid>
                      </Grid>
                      <Grid container spacing={1} style={{ justifyContent: 'space-between', marginTop: 5 }}>
                        <Grid item>電話號碼:</Grid>
                        <Grid item>
                          <Box textAlign="right">{data?.delivery?.phone || ''}</Box>
                        </Grid>
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid container spacing={1} style={{ justifyContent: 'space-between', marginTop: 5 }}>
                        <Grid item>指定取貨方式:</Grid>
                        <Grid item>
                          <Box textAlign="right" fontWeight="bold">{ `【${config.BrandNameTW}】${fridgeDictionary[data?.fridgeId]?.name || ''}`}</Box>
                          <Box textAlign="right" fontSize="14px">
                            {fridgeDictionary[data?.fridgeId]?.address || ''}
                          </Box>
                        </Grid>
                      </Grid>
                      <Grid container spacing={1} style={{ justifyContent: 'space-between', marginTop: 5 }}>
                        <Grid item>取貨時間:</Grid>
                        <Grid item>
                          <Box color="rgb(168,0,0)" fontWeight="bold" my={1}>
                            {
                              (() => {
                                const preOrderArrivedNotificationTime = (
                                  data?.batches?.[data?.batch]?.preOrderArrivedNotificationTime
                                   || data ?.preOrderArrivedNotificationTime
                                   || preOrder?.batches?.[data?.batch]
                                    ?.preOrderArrivedNotificationTime
                                   || preOrder?.preOrderArrivedNotificationTime
                                   || null
                                );
                                const redeemEndTime = (
                                  data?.batches?.[data?.batch]?.redeemEndTime
                                  || data?.redeemEndTime
                                  || preOrder?.batches?.[data?.batch]?.redeemEndTime
                                  || preOrder?.redeemEndTime
                                  || null
                                );
                                if (
                                  !preOrderArrivedNotificationTime
                                    || !redeemEndTime
                                ) return null;
                                return `${moment(preOrderArrivedNotificationTime).format('YYYY-MM-DD HH:mm')} ~ ${moment(redeemEndTime).format('YYYY-MM-DD HH:mm')}`;
                              })()
                            }
                          </Box>
                        </Grid>
                      </Grid>
                    </>
                  )
                }
              </Box>
            </Box>,
            <ExpandableContent
              key={`${preOrderIndex}_preOrderRules`}
              className={checkoutData.length === 1 ? 'open' : ''}
              onClick={(event) => {
                if (event.target.className.indexOf('open') < 0) {
                  event.target.className += ' open';
                } else {
                  event.target.className = event.target.className.replace(' open', '');
                }
              }}
            >
              <Rule />
            </ExpandableContent>,
          ];
        })
      }
    </Box>
  );

  const receiptFooter = (
    <Hidden only="sm">
      <Box
        width={1}
        position="fixed"
        left={0}
        bottom={0}
        bgcolor="common.white"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom, 0)',
        }}
      >
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => history.replace('/pre-orders')}
        >
          <Box
            py={2}
            fontSize="body1.fontSize"
            fontWeight="fontWeightBold"
          >
            預購完成
          </Box>
        </Button>
      </Box>
    </Hidden>
  );

  return (
    <Box
      minHeight="calc(100vh - 100px)"
      bgcolor={isInDesktop ? 'white' : 'rgb(242, 242, 242)'}
      pb={isInDesktop ? 1 : 5}
    >
      {receiptHeader}
      {receiptContent}
      {receiptFooter}
      {(isLoading) && <ProgressBackdrop isShow />}
      {
          alert && (
            <Alert
              isOpen
              title={alert?.title || ''}
              content={alert?.message || ''}
              buttons={alert?.buttons || (
                <Button fullWidth onClick={() => setAlert(null)}>
                  <Box fontWeight="fontWeightBold" color="support.main">
                    確認
                  </Box>
                </Button>
              )}
            />
          )
        }
    </Box>
  );
};

export default Receipt;
